import type { NomadDB } from '~/db/NomadDB';
import { getServices } from '~/db/services';
import { getToDayDateIso, getPreviousDayDateIso } from '~/lib/utils/date';
import type { CoachingStatSchema } from '~/types/schema';

export const getCurrentHistoryDate = () => {
  const date = new Date();
  const day = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).toISOString();
  return day;
};

const createCoachingStat = (
  memberId: string,
  coachingPlan: ProfileCoachingPlan,
  libraryBookId: string,
  isTotal: boolean = false
): CoachingStatSchema => {
  const historyDate = getCurrentHistoryDate();
  const historyDateUTC = getToDayDateIso();

  const coachingStat: CoachingStatSchema = {
    appId: libraryBookId,
    member: memberId,
    historyDate,
    historyDateUTC,
    objectiveValue: coachingPlan?.objectiveValue || '',

    minutesPerWeek: coachingPlan.minutesPerWeek,
    isTotal,
    total: isTotal ? 1 : 0,
    local: 1,

    minutes: 0, // min par jour
    totalInMinutes: 0, // duree total soit lb soit app
  };
  if (!isTotal) {
    coachingStat.dailyMinutesForActive = coachingPlan.dailyMinutesForActive;
    coachingStat.daysPerWeek = coachingPlan.daysPerWeek;
    coachingStat.averageGrade = 0; // score des exercices faits du lb ( 0 à 1 00 )
    coachingStat.globalScore = 0; // prgression nombre d'exercises faits / total ( 0 à 100 )
    coachingStat.streak = 1;
  } else {
    coachingStat.totalInMinutesThisWeek = 0;
  }
  return coachingStat;
};

export const getCoachingStat = async (
  db: NomadDB,
  memberId: string,
  coachingPlan: ProfileCoachingPlan,
  libraryBookId: string,
  isTotal: boolean = false
): Promise<CoachingStatSchema> => {
  const { coachingStatsService } = getServices(db);
  const toDay = getToDayDateIso();
  const yesterDay = getPreviousDayDateIso(toDay);
  let lastCoachingStat: CoachingStatSchema;
  if (isTotal) {
    lastCoachingStat = await coachingStatsService.findLastTotal();
  } else {
    lastCoachingStat = await coachingStatsService.findLastByLb(libraryBookId);
  }

  // already a coachingstat of the day return it.
  if (lastCoachingStat && toDay === lastCoachingStat.historyDateUTC) {
    return lastCoachingStat;
  }

  let nextCoachingStat: CoachingStatSchema;
  if (!lastCoachingStat) {
    nextCoachingStat = createCoachingStat(
      memberId,
      coachingPlan,
      libraryBookId,
      isTotal
    );
  } else {
    nextCoachingStat = {
      ...lastCoachingStat,
      historyDate: getCurrentHistoryDate(),
      historyDateUTC: toDay,
      minutes: 0,
    };

    if (isTotal) {
      const dateLastDay = new Date(lastCoachingStat.historyDateUTC);
      const dateCurrentDay = new Date(toDay);
      const lastDay = dateLastDay.getDay() === 0 ? 6 : dateLastDay.getDay() - 1;
      const currentDay =
        dateCurrentDay.getDay() === 0 ? 6 : dateCurrentDay.getDay() - 1;
      const diff = dateCurrentDay.getTime() - dateLastDay.getTime();
      if (currentDay <= lastDay || diff >= 1000 * 60 * 60 * 24 * 7) {
        nextCoachingStat.totalInMinutesThisWeek = 0;
      }
    } else if (lastCoachingStat.historyDateUTC === yesterDay) {
      nextCoachingStat.streak += 1;
    } else {
      nextCoachingStat.streak = 1;
    }
  }

  return nextCoachingStat;
};

const getPercentValue = (value) => {
  value = value < 0.01 && value > 0 ? 0.01 : value;
  value = value > 0.99 ? 1 : value;
  return Math.round(value * 100);
};

const retrieveCoachingStatsFromExerciseInfos = async (
  db: NomadDB,
  libraryBookId: string,
  memberId: string
) => {
  const { exerciseInfosService } = getServices(db);

  const infos = await exerciseInfosService.findByMemberIdAndLibraryBookId(
    libraryBookId,
    memberId
  );

  let averageGrade = 0;
  let globalScore = 0;

  if (infos.length > 0) {
    const finishedExercises = infos.filter((info) => info.isFinished === 1);
    globalScore = finishedExercises.length / infos.length;
    if (finishedExercises.length) {
      averageGrade =
        finishedExercises.reduce((acc, curr) => (acc += curr.score), 0) /
        finishedExercises.length;
    }
  }

  return {
    averageGrade: getPercentValue(averageGrade), // score des exercices faits du lb ( 0 à 100 )
    globalScore: getPercentValue(globalScore), // prgression nombre d'exercises faits / total ( 0 à 100 )
  };
};

export const incTimeTotal = async (
  db: NomadDB,
  memberId: string,
  coachingPlan: ProfileCoachingPlan,
  appId: string,
  incTime: number
) => {
  const { coachingStatsService } = getServices(db);

  const coachingStat = await getCoachingStat(
    db,
    memberId,
    coachingPlan,
    appId,
    true
  );

  const newCoachingStat = {
    ...coachingStat,
    totalInMinutes: coachingStat.totalInMinutes + incTime,
    minutes: coachingStat.minutes + incTime,
    totalInMinutesThisWeek: coachingStat.totalInMinutesThisWeek + incTime,
    local: 1,
  };

  await coachingStatsService.put(newCoachingStat);

  return newCoachingStat;
};

export const incTimeLb = async (
  db: NomadDB,
  memberId: string,
  coachingPlan: ProfileCoachingPlan,
  libraryBookId: string,
  incTime: number
): Promise<CoachingStatSchema> => {
  const { coachingStatsService } = getServices(db);

  const coachingStat = await getCoachingStat(
    db,
    memberId,
    coachingPlan,
    libraryBookId,
    false
  );

  let newCoachingStat: CoachingStatSchema = {
    ...coachingStat,
    local: 1,
  };

  if (coachingStat.averageGrade === 0) {
    const { averageGrade, globalScore } =
      await retrieveCoachingStatsFromExerciseInfos(db, libraryBookId, memberId);
    newCoachingStat = {
      ...newCoachingStat,
      averageGrade,
      globalScore,
    };
  }

  newCoachingStat = {
    ...newCoachingStat,
    totalInMinutes: coachingStat.totalInMinutes + incTime,
    minutes: coachingStat.minutes + incTime,
    local: 1,
  };

  await coachingStatsService.put(newCoachingStat);

  return newCoachingStat;
};

export const updateScore = async (
  db: NomadDB,
  memberId: string,
  coachingPlan: ProfileCoachingPlan,
  libraryBookId: string
): Promise<CoachingStatSchema> => {
  const { coachingStatsService } = getServices(db);

  const coachingStat = await getCoachingStat(
    db,
    memberId,
    coachingPlan,
    libraryBookId,
    false
  );

  const { averageGrade, globalScore } =
    await retrieveCoachingStatsFromExerciseInfos(db, libraryBookId, memberId);

  const newCoachingStat: CoachingStatSchema = {
    ...coachingStat,
    averageGrade,
    globalScore,
    local: 1,
  };

  await coachingStatsService.put(newCoachingStat);

  return newCoachingStat;
};
